import { useCallback, useEffect, useMemo } from 'react';
import { uniq } from 'lodash';
import { featureFlags } from 'env';
import useAuth from 'hooks/useAuth';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { useUserClient } from 'client/client-hook';
import { analytics } from 'lib/analytics';
import { fetchAnnouncementState, saveReceipts } from '../api';
import { LOAD_STATUS } from '../constants';
import { useLivePosts, warnUnknownReleases } from '../livePosts';
import { chipMenuIds, hasReceipt, hintPosts, unreadPosts, visiblePosts } from '../rules';
import { useAnnouncementStore } from '../store';
import type { PanelSource, Post, RuleContext, Surface } from '../types';

const NO_POSTS: Post[] = [];
const NO_IDS: Set<string> = new Set();

/** Loads this person's What's New state once per session; layout remounts and token refreshes don't refetch. */
export function useLoadAnnouncements(): void {
  const { user } = useAuth();
  const getUserClient = useUserClient();
  const userId = user?.tcId;

  useEffect(() => {
    if (!userId) return;
    const store = useAnnouncementStore.getState();
    if (store.userId === userId && store.status !== LOAD_STATUS.idle) return;
    store.startLoad(userId);
    getUserClient()
      .then((client) => fetchAnnouncementState(client, userId))
      .then(
        (state) => {
          warnUnknownReleases(state.releases);
          useAnnouncementStore.getState().finishLoad(userId, state);
        },
        () => useAnnouncementStore.getState().finishLoad(userId, null)
      );
  }, [userId, getUserClient]);
}

/** Records where posts were seen: in the store at once, so the UI updates, then on the server in the background. */
export function useMarkSeen(): (postIds: string[], surface: Surface) => void {
  const { user } = useAuth();
  const getUserClient = useUserClient();
  const userId = user?.tcId;

  return useCallback(
    (postIds, surface) => {
      const { receipts, addReceipts } = useAnnouncementStore.getState();
      const fresh = uniq(postIds).filter((postId) => !hasReceipt(receipts, postId, surface));
      if (!userId || !fresh.length) return;
      const seenAt = Date.now();
      addReceipts(fresh.map((postId) => ({ postId, surface, seenAt })));
      getUserClient()
        .then((client) => saveReceipts(client, userId, fresh, surface))
        .catch(() => undefined);
    },
    [userId, getUserClient]
  );
}

/** The rules' view of this person; null until their state has loaded (or failed to). */
export function useRuleContext(): RuleContext | null {
  const { user } = useAuth();
  const { isPaid, isEntitled } = useSubscriptionStatus();
  const status = useAnnouncementStore((s) => s.status);
  const now = useAnnouncementStore((s) => s.now);
  const signedUpAt = useAnnouncementStore((s) => s.signedUpAt);
  const receipts = useAnnouncementStore((s) => s.receipts);
  const roles = user?.roles;

  return useMemo(() => {
    if (status !== LOAD_STATUS.ready && status !== LOAD_STATUS.failed) return null;
    // Complimentary access is known from roles at once; otherwise null until billing answers.
    const entitled = isEntitled ? true : isPaid === null ? null : false;
    return { now, signedUpAt, roles: roles ?? [], entitled, flags: featureFlags, receipts };
  }, [status, now, signedUpAt, receipts, roles, isPaid, isEntitled]);
}

/** The rules' view only once the state has loaded: without it, unread, chips and hints can't be told from read. */
export function useReadyRuleContext(): RuleContext | null {
  const ctx = useRuleContext();
  const ready = useAnnouncementStore((s) => s.status === LOAD_STATUS.ready);
  return ready ? ctx : null;
}

// One rule over every post, or `none` until the state has loaded; `none` must be a constant.
function useReadyRule<T>(rule: (posts: Post[], ctx: RuleContext) => T, none: T): T {
  const ctx = useReadyRuleContext();
  const posts = useLivePosts();
  return useMemo(() => (ctx ? rule(posts, ctx) : none), [ctx, posts, rule, none]);
}

/** The posts in the panel, newest first; null while this person's state is still loading. */
export function useVisiblePosts(): Post[] | null {
  const ctx = useRuleContext();
  const posts = useLivePosts();
  return useMemo(() => (ctx ? visiblePosts(posts, ctx) : null), [ctx, posts]);
}

/** Posts on the What's new badge. */
export const useUnreadPosts = (): Post[] => useReadyRule(unreadPosts, NO_POSTS);

/** Menu item ids that show a NEW chip. */
export const useNewMenuIds = (): Set<string> => useReadyRule(chipMenuIds, NO_IDS);

/** Posts whose page hint may still show. */
export const useHintPosts = (): Post[] => useReadyRule(hintPosts, NO_POSTS);

/** The unread count for a What's new entry point, and how it opens the panel (recorded with where from). */
export function useWhatsNew(): { unread: number; open: (source: PanelSource) => void } {
  const unread = useUnreadPosts().length;
  const open = useCallback(
    (source: PanelSource) => {
      analytics.whatsNewOpened({ unread, source });
      useAnnouncementStore.getState().setPanelOpen(true);
    },
    [unread]
  );
  return { unread, open };
}
